import AnalyticsEnums from './analytics/enums';

export type TPageData = {
  title: string;
  path: string;
  message: string;
};
export type TRouteChange = {
  to: string;
  from: string;
};

const event = { ...AnalyticsEnums } as const;
export type TEvent = typeof event;
export type TCategory = keyof TEvent;
export type TSubCategory<C extends TCategory> = keyof TEvent[C];
export default defineNuxtPlugin({
  name: 'event-tracking',
  dependsOn: ['init-client', 'directives'],
  parallel: true,
  setup() {
    const { $enabled } = useNuxtApp();
    const gtmEnabled = computed(() =>
      $enabled('tracking.enablegoogletagmanagertracking'),
    );
    const singleEventTracking = <
      C extends TCategory,
      S extends TSubCategory<C>,
      T,
    >(
      enumCategory: C,
      eventEnumKey: S,
      eventParams?: T,
    ): void => {
      if (!!window?.gtag && gtmEnabled.value) {
        window?.gtag('event', event?.[enumCategory]?.[eventEnumKey], {
          ...eventParams,
        });
      } else
        window?.dataLayer?.push({
          event: event?.[enumCategory]?.[eventEnumKey],
          ...eventParams,
        });
      if (!gtmEnabled.value)
        window?.srtmCommands?.push({
          event: event?.[enumCategory]?.[eventEnumKey] as string,
          payload: { ...eventParams },
        });
      if (eventEnumKey === 'LOGIN_SUCCESS' || eventEnumKey === 'LOGIN_INIT')
        trackLogin(eventParams);
      if (eventEnumKey === 'REG_SUCCESS' || eventEnumKey === 'REG_INIT')
        trackRegSuccess(eventParams);
    };

    //   explicit eventing
    const trackLogin = <T>(eventParams: T) => {
      if (gtmEnabled.value) return;
      window?.srtmCommands?.push({
        event: 'track.user.login',
        payload: {
          action: 'complete',
          userId: window.getUserId(),
          ...eventParams,
        },
      });
      if (!!window?.tracking?.tradedesk?.fn?.login)
        window?.tracking?.tradedesk?.fn?.login();
      if (!!window?.tracking?.flashtalking?.fn?.login)
        window?.tracking?.flashtalking?.fn?.login();
      if (!!window?.otag) window?.otag('event', 'login');

      if (!!window?.fbq)
        window?.fbq('track', 'Login', {
          currency: useSiteStore().getCurrencyCode,
          value: 1,
          external_id: window.getUserId(),
        });
    };
    const trackRegIntent = <T>(eventParams: T) => {
      if (gtmEnabled.value) return;
      window?.srtmCommands?.push({
        event: 'track.user.registration',
        payload: {
          action: 'start',
          ...eventParams,
        },
      });
      if (!!window?.tracking?.tradedesk?.fn?.regEngaged)
        window?.tracking?.tradedesk?.fn?.regEngaged();
      if (!!window?.tracking?.flashtalking?.fn?.regEngaged)
        window?.tracking?.flashtalking?.fn?.regEngaged();
    };
    const trackRegSuccess = <T>(eventParams: T) => {
      if (gtmEnabled.value) return;
      window?.srtmCommands?.push({
        event: 'track.user.registration',
        payload: {
          action: 'complete',
          userId: window.getUserId(),
          ...eventParams,
        },
      });
      if (!!window?.tracking?.tradedesk?.fn?.regCompleted)
        window?.tracking?.tradedesk?.fn?.regCompleted();
      if (!!window?.tracking?.flashtalking?.fn?.regCompleted)
        window?.tracking?.flashtalking?.fn?.regCompleted();
      if (!!window?.otag) window?.otag('event', 'registration');
      if (!!window?.fbq)
        window?.fbq('track', 'Complete Registration', {
          currency: useSiteStore().getCurrencyCode,
          value: 1,
          external_id: window.getUserId(),
        });
    };

    const errorTracking = (eventParams: TPageData): void => {
      window?.dataLayer?.push({
        event: 'page_view',
        ...eventParams,
      });
    };

    const trackRouteChange = (pathParams: TRouteChange): void => {
      window?.dataLayer?.push({
        event: 'page_view',
        ...pathParams,
      });
      if (!gtmEnabled.value) {
        window?.srtmCommands?.push({
          event: 'track.pageview',
          payload: { ...pathParams },
        });
        window?.tracking?.tradedesk?.fn?.pageView();
        window.tracking?.flashtalking?.fn?.pageView();

        if (!!window?.fbq) window?.fbq('track', 'PageView');
      }

      const action = window?.dtrum?.enterAction(
        `navigate`,
        null,
        null,
        pathParams.to,
      );
      window?.dtrum?.leaveAction(action);
    };

    return {
      provide: {
        trackEvent: singleEventTracking,
        errorTracking,
        trackRouteChange,
        //   explicit eventing
        trackLogin,
        trackRegIntent,
        trackRegSuccess,
      },
    };
  },
});
